<script module lang="ts">
	const minConsent: ConsentLevel[] = [1];
	let chosenLevels: ConsentAnswer = $state(minConsent);
	let chosenNotConfirmedLevels: boolean[] = $state([true, false, false, false]);
	let prevAnswer: ConsentAnswer | undefined;
	let logTimeout: ReturnType<typeof setTimeout> | undefined;
	let hasOpenCookieBanner = $state(false);
	export const getHasOpenCookieBanner = () => hasOpenCookieBanner;
</script>

<script lang="ts">
	import { onMount } from "svelte";
	import { afterNavigate } from "$app/navigation";
	import storageAvailable from "#lib/utils/storageAvailable.js";
	import consentAnswerStore, {
		administrateCookieChoice,
		versionAppName,
		versionDate as version,
		type ConsentLevel,
		type ConsentAnswer,
	} from "#lib/cookieConsent.svelte.js";
	import Checkbox from "#lib/InputCheckbox.svelte";
	import { syncSessionValues } from "#lib/sessionValues.js";
	// Third party tracking
	import FacebookPixelScript from "#lib/FacebookPixelScript.svelte";
	import GoogleTagManager from "#lib/GoogleTagManager.svelte";
	import HubSpotTrackingScript from "#lib/HubSpotTrackingScript.svelte";

	const storageLevelKey = `cookieLevels${version.replace(/-/g, "")}`;
	// Cookie consent levels
	const consentLevels = new Map([
		// Keep all level items, commented out if not needed for reference
		[
			1,
			{
				name: "necessary",
				label: "Nødvendige",
			},
		],
		[
			2,
			{
				name: "functional",
				label: "Funksjonelle",
			},
		],
		[
			3,
			{
				name: "statistics",
				label: "Statistikk",
			},
		],
		[
			4,
			{
				name: "marketing",
				label: "Markedsføring",
			},
		],
	]);

	const storeTrackingLocalStorage = (value: "true" | "false" | "") => {
		// Use localStorage and the following key for Google consent mode
		if (hasLocalStorage) localStorage.setItem("consentGranted", value);
	};

	const setChosenNotConfirmedLevels = (consentAnswer: ConsentAnswer) => {
		return chosenNotConfirmedLevels.map((_: boolean, i: number) =>
			i > 0 ? consentAnswer.includes((i + 1) as ConsentLevel) : true
		);
	};

	// POST to log
	const logConsentChoice = async (choice: ConsentAnswer) => {
		const formChoice = Array.from(consentLevels.keys())
			.filter((level) => choice.includes(level as ConsentLevel))
			.map((level) => {
				const levelVals = consentLevels.get(level);
				return levelVals ? levelVals.name : "";
			})
			.join(", ");

		// Only log if change (commented out as it seems to make sense to log last active choice)
		// if ($sessionValues.cookie_choice && $sessionValues.cookie_choice === formChoice) return;

		const data = await fetch("/api/consent", {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Credentials: "same-origin",
				// Cookie: document.cookie,
			},
			body: JSON.stringify({
				choice: formChoice,
				version: `${versionAppName}_${version}`,
				clientDatetime: new Date().toLocaleString("sv"), // `sv` for ISO like, but locale
				ua: navigator.userAgent,
			}),
		})
			.then((res) => {
				if (!res.ok) throw new Error("Failed log attempt");
				else return res.json();
			})
			.catch((error) => {
				if (typeof error === "string") console.warn(error);
			});

		if (!data) return;

		let consentValues: { [key: string]: string } = {};
		let dev = false;

		if (data instanceof Array)
			data.forEach((item: [string, string], i) => {
				const key: string = `cookie_${item[0]}`;
				if (i < 4 && item[0] && item[1]) consentValues[key] = item[1];
				else if (item[0] === "dev") dev = !!item[1];
			});
		if (dev) console.table(data);

		// Store consent data in browser storage for reference
		syncSessionValues(consentValues, { useBrowserStorage: true });
	};

	const setConsentAnswer = (value: "true" | "false" | "null") => {
		clearTimeout(logTimeout);

		// if "true", set all levels
		if (value === "true") {
			chosenLevels = Array.from(consentLevels.keys()) as ConsentAnswer;
			chosenNotConfirmedLevels = setChosenNotConfirmedLevels(chosenLevels);
		} else {
			if (value === "null") chosenNotConfirmedLevels = setChosenNotConfirmedLevels(minConsent);

			chosenLevels = chosenNotConfirmedLevels
				.map((state: boolean, i: number) => state && i + 1)
				.filter((x) => x) as ConsentAnswer;
		}

		consentAnswerStore.set(chosenLevels);

		if (hasLocalStorage) {
			localStorage.setItem(storageLevelKey, JSON.stringify(chosenLevels));
		}

		// Store consent in log
		logTimeout = setTimeout(() => {
			logConsentChoice(chosenLevels);
		}, 1000);
	};

	let {
		banner = false,
		alternatives = {
			null: "Avslå",
			false: "Bruk valgte",
			true: "Godta alle",
		},
		class: classes = "",
		children,

		// Show as cookie banner, else: normal flow
		// Button alternatives to user
	}: {
		banner?: boolean;
		alternatives?: Record<"false" | "true" | "null", string>;
		class?: string;
		children?: import("svelte").Snippet;
	} = $props();
	let hasNavigatedAfterAnswer = $state(false);
	let hasPreviouslyAnswered = $state(false);
	let hasAnswered = $state(false);
	let visuallyHidden = $state(true);
	let hasLocalStorage = $state(false);
	let hasFunctionalRequirements = $state(false);
	let hasCookiesEnabled = false;
	let trackingAccepted = $state(false);
	let functionalAccepted = $state(false);
	let analyticsAccepted = $state(false);
	let hideTimeout: ReturnType<typeof setTimeout> | undefined = $state();
	let removeBannerFromDom = $derived(
		banner &&
			(!hasFunctionalRequirements || hasPreviouslyAnswered || hasNavigatedAfterAnswer) &&
			!$administrateCookieChoice
	);

	$effect(() => {
		// set hasOpenCookieBanner
		if (!visuallyHidden && !removeBannerFromDom) hasOpenCookieBanner = true;
		else hasOpenCookieBanner = false;
	});

	$effect(() => {
		// A11y, when reopening banner, focus to banner's first focusable element
		const focusable =
			$administrateCookieChoice && !removeBannerFromDom
				? document?.querySelectorAll(".panel-wrapper :is(a, input, button)")
				: [];
		if (focusable.length > 0) (focusable[0] as HTMLElement).focus();
	});

	$effect(() => {
		functionalAccepted = chosenLevels.includes(2);
		analyticsAccepted = chosenLevels.includes(3);
		trackingAccepted = chosenLevels.includes(4);
	});

	administrateCookieChoice.subscribe((val) => {
		if (val && hideTimeout) clearTimeout(hideTimeout);
		if (val && logTimeout) clearTimeout(logTimeout);
		if (val) {
			hasAnswered = false;
			visuallyHidden = false;
			hasNavigatedAfterAnswer = false;
		}
	});

	consentAnswerStore.subscribe((value: ConsentAnswer) => {
		if (value?.includes(4)) {
			storeTrackingLocalStorage(trackingAccepted ? "true" : "false");
		} else {
			storeTrackingLocalStorage("");
		}
	});

	afterNavigate(({ shallow, type }) => {
		if (shallow && type === "goto") return;

		hasNavigatedAfterAnswer = hasAnswered ? true : false;
	});

	/* Checks for if should ask for tracking consent */
	onMount(() => {
		const navigator = (typeof window !== "undefined" ? window.navigator : null) as Navigator & {
			globalPrivacyControl: undefined | boolean;
		};

		hasLocalStorage = storageAvailable("localStorage");
		hasCookiesEnabled = !!navigator?.cookieEnabled;
		hasFunctionalRequirements = hasLocalStorage && hasCookiesEnabled;

		if (hasLocalStorage && typeof prevAnswer === "undefined") {
			prevAnswer = JSON.parse(
				localStorage.getItem(storageLevelKey) || JSON.stringify([])
			) as ConsentAnswer;
		}

		if (prevAnswer?.length) {
			hasPreviouslyAnswered = true;
			chosenLevels = prevAnswer;
			chosenNotConfirmedLevels = setChosenNotConfirmedLevels(prevAnswer);
			consentAnswerStore.set(prevAnswer);
		} else if (!hasFunctionalRequirements) {
			hasPreviouslyAnswered = true;
			consentAnswerStore.set(minConsent);
		} else {
			// Tracking consent is visible if not removed from DOM
			visuallyHidden = false;
		}
	});
</script>

{#if !removeBannerFromDom}
	<div
		class:panel-wrapper={banner}
		class={classes}
		class:answered-banner={banner && hasAnswered}
		class:sr-only={banner && visuallyHidden}
	>
		<div
			class:panel={banner}
			class="grid items-center gap-x-[.5rem] tablet:gap-y-[.5rem] laptop:gap-4"
		>
			<p class="my-0 mx-[0_0.5rem] max-w-prose text-sm tablet:text-[1em]">
				{@render children?.()}
			</p>

			<form action="" class="grid gap-.5rem laptop:grid-cols-2">
				<ol class="list-none m-0 p-0 flex gap-x-.5rem text-sm">
					{#each consentLevels as [level, { label }] (level)}
						<li>
							<label class="min-h-3rem inline-flex items-center gap-[.1ch] text-sm">
								<Checkbox
									name="add"
									value={level}
									bind:state={chosenNotConfirmedLevels[level - 1]}
									disabled={level === 1}
								/>
								<span class="text-[clamp(.7em,2.5vw,1em)] leading-none">
									{label}
								</span>
							</label>
						</li>
					{/each}
				</ol>

				<div>
					<p class="my-0 grid grid-cols-3 gap-.5rem children-px-1">
						{#each Object.entries(alternatives) as [val, text] (val)}
							{@const choice = val === "null" ? "null" : val === "true" ? "true" : "false"}
							<button
								type="button"
								class="button whitespace-nowrap min-h-48px text-sm"
								class:button-disabled={!hasFunctionalRequirements}
								onclick={() => {
									if (hideTimeout) clearTimeout(hideTimeout);

									setConsentAnswer(choice);
									hasAnswered = true;
									administrateCookieChoice.set(false);

									if (banner) {
										hideTimeout = setTimeout(() => (visuallyHidden = true), 1000);
										// time matches CSS animation total time
									}
								}}
							>
								{text}
							</button>
						{/each}
					</p>

					{#if banner}
						<small>
							<p class="my-1 text-sm text-center relative top-.5 text-[--fg-muted]">
								Cookie-valg kan endres nederst på siden
							</p>
						</small>
					{/if}
				</div>
			</form>
		</div>
	</div>
{/if}

{#if banner}
	<!-- Google Consent Mode v2: analyticsAccepted → analytics_storage (level 3 Statistikk);
	     marketingAccepted → ad_storage + ad_user_data + ad_personalization (level 4 Markedsføring);
	     functionality_storage + personalization_storage ride with EITHER via OR-logic inside
	     buildConsentUpdate. hasLocalStorage gates both so we don't emit signals we can't persist.
	     See googleConsent.ts for the full signal map. -->
	<GoogleTagManager
		analyticsAccepted={hasLocalStorage && analyticsAccepted}
		marketingAccepted={hasLocalStorage && trackingAccepted}
	/>
	<FacebookPixelScript track={trackingAccepted} />
	<!-- alwaysLoad: HubSpot loads in necessary-only mode immediately,
	     consent state is forwarded as users opt in or revoke. -->
	<HubSpotTrackingScript
		alwaysLoad
		{functionalAccepted}
		{analyticsAccepted}
		marketingAccepted={trackingAccepted}
	/>
{/if}

<style lang="postcss">
	.panel-wrapper {
		position: fixed;
		bottom: 0;
		padding-block: 3px 4vh;
		width: 100%;
		z-index: 10;
		display: flex;
		justify-content: center;
		background-image: linear-gradient(transparent, hsl(from var(--aurora) h s l / 0.625));
	}

	.panel {
		margin-inline: var(--space-edge-x);
		border-radius: 1rem;
		width: fit-content;
		max-width: 100%;
		background-color: var(--skodde);
		color: var(--tundra);
		box-shadow:
			0 0 0 3px var(--lysning),
			0 0 0.5rem 3px #0005;
		padding-block: var(--s-2);
		padding-inline: var(--s-3);
		/* Cookie img (assumes img equal in width and height) */
		--img-size: calc(1.375rem + 1vh);
		background-position: center calc(100% + (var(--img-size) - 1.75rem + 1vh));
		background-size: auto var(--img-size);
		/* Ensure content is alwyas visible with a max-height and scrollable area */
		max-height: 75vh;
		max-height: 75dvh;
		overflow-y: auto;
	}

	@media (--tablet) {
		.panel {
			/* Cookie img (assumes square img) */
			--img-size: 2.625rem;
			background-position: max(2.5vw, 0.75rem) center;
			padding-block: var(--s-3) var(--s-2);
			padding-inline: max(6vw, 5rem) max(2.5vw, 1rem);
			background-image: url("/_assets/cookie.svg");
		}
	}

	.answered-banner {
		overflow: hidden;
		animation: fade-out 0.5s 0.5s var(--easing) forwards;
		& > * {
			animation: answered-banner 0.75s 0.25s var(--easing) forwards;
		}
	}

	@keyframes fade-out {
		0% {
			opacity: 1;
		}
		100% {
			opacity: 0;
		}
	}

	@keyframes answered-banner {
		0% {
			opacity: 1;
			transform: translateY(0);
		}
		75% {
			opacity: 0;
		}
		100% {
			transform: translateY(200%);
		}
	}
</style>
